import { useState } from "react";
import type { GuessEntry } from "@mk/shared";
import { renderShareCard } from "../features/share/shareCard.ts";
import { shareResult } from "../features/share/share.ts";
import {
  shareEyebrow,
  shareMessage,
  shareText,
  shareTitle,
  timedShareSummary,
  type ShareResult,
} from "../features/share/shareText.ts";
import { messages } from "../i18n/index.ts";

interface ShareButtonProps {
  result: ShareResult;
  rows: readonly GuessEntry[];
  /** Düelloda rakibin tahtası — görselde iki tahta yan yana çiziliyor. */
  opponentRows?: readonly GuessEntry[];
  /**
   * Görseldeki harfler cevabı sızdırır mı? Günlük kelimede evet: aynı kelimeyi
   * herkes oynuyor. Pratik ve düelloda kelime kişiye özel, sakıncası yok.
   */
  spoilerRisk?: boolean;
  className?: string;
}

const OUTCOME_NOTICE: Record<string, string | null> = {
  shared: null,
  cancelled: null,
  copied: messages.share.copied,
  downloaded: messages.share.downloaded,
  failed: messages.share.failed,
};

export function ShareButton({
  result,
  rows,
  opponentRows,
  spoilerRisk = false,
  className,
}: ShareButtonProps) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Spoiler riski varsa harfler varsayılan olarak KAPALI.
  const [showLetters, setShowLetters] = useState(!spoilerRisk);

  async function onShare() {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const image = await renderShareCard({
        eyebrow: shareEyebrow(result),
        message: shareMessage(result),
        rows,
        // Düelloda iki tahta yan yana; maç bittiği için harfler açık.
        opponent:
          result.mode === "duel" && opponentRows
            ? { label: result.opponentName, rows: opponentRows }
            : undefined,
        showLetters,
        summary: result.mode === "timed" ? timedShareSummary(result) : undefined,
      });
      const outcome = await shareResult(image, shareText(result, rows), shareTitle(result));
      setNotice(OUTCOME_NOTICE[outcome] ?? null);
    } catch {
      setNotice(messages.share.imageFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="share-block">
      <button
        type="button"
        className={className ?? "btn-secondary"}
        onClick={() => void onShare()}
        disabled={busy}
      >
        {busy ? messages.share.preparing : messages.share.result}
      </button>

      {spoilerRisk && (
        <label className="share-toggle">
          <input
            type="checkbox"
            checked={showLetters}
            onChange={(event) => setShowLetters(event.target.checked)}
          />
          <span>
            {messages.share.showLetters}
            <span className="share-toggle__hint">
              {messages.share.spoilerHint}
            </span>
          </span>
        </label>
      )}

      {notice && (
        <p className="share-notice" role="status">
          {notice}
        </p>
      )}
    </div>
  );
}
